import React, { useEffect, useState, useCallback } from 'react';
import css from './StartYourProjectPage.module.css';
import { LayoutSingleColumn, Page } from '../../components';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { injectIntl } from 'react-intl';
import { logout } from '../../ducks/auth.duck';
import { withRouter } from 'react-router-dom';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { signup } from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import FooterContainer from '../FooterContainer/FooterContainer';
import CustomNavbar from '../CustomLandingPage/components/CustomNavbar/CustomNavbar';
import { IdlePromptPopup } from '../../components';
import SignupForm from '../AuthenticationPage/SignupForm/SignupForm';
import TermsAndConditions from '../AuthenticationPage/TermsAndConditions/TermsAndConditions';

const START_PROJECT_NAV_LINKS = [
  { label: 'Marketplace', href: '/home-designs' },
  { label: 'About', href: '/about' },
  { label: 'FAQ', href: '/faq' },
];
import QuestionSection from '../CustomLandingPage/components/QuestionSection/QuestionSection';
import BottomPageBanner from '../../components/BottomPageBanner/BottomPageBanner';
import HeroSection from './components/HeroSection/HeroSection';
import WhatHappensSection from './components/WhatHappensSection/WhatHappensSection';
import GetStartedSection from './components/GetStartedSection/GetStartedSection';
import PlanningDataSection from './components/PlanningDataSection/PlanningDataSection';

const PP_REF_SESSION_KEY = 'startYourProject_ppRef';

const StartYourProjectPageComponent = props => {
  const { currentUser, isAuthenticated, onLogout, onUpdateProfile, history, onManageDisableScrolling, onSubmitSignup, intl } = props;
  const [ppRef, setPpRef] = useState('');

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  const handleStartProject = useCallback((ref) => {
    const value = ref !== undefined ? ref : ppRef;

    if (isAuthenticated) {
      // Already logged in — save directly to profile and redirect home
      if (value && value.trim()) {
        onUpdateProfile({ publicData: { planningReference: value.trim() } });
      }
      history.push('/');
      return;
    }

    // Not logged in — store ref and go to signup
    if (value && value.trim()) {
      try { sessionStorage.setItem(PP_REF_SESSION_KEY, value.trim()); } catch (e) {}
    } else {
      try { sessionStorage.removeItem(PP_REF_SESSION_KEY); } catch (e) {}
    }
    history.push('/signup-customer');
  }, [ppRef, isAuthenticated, onUpdateProfile, history]);

  const handleContinueAnyway = useCallback(() => {
    if (isAuthenticated) {
      history.push('/');
      return;
    }
    try { sessionStorage.removeItem(PP_REF_SESSION_KEY); } catch (e) {}
    history.push('/signup-customer');
  }, [isAuthenticated, history]);

  return (
    <Page
      title="Start Your Project — Register Your Planning Application"
      description="Enter your planning reference to register your project and get matched with the right suppliers."
      scrollingDisabled={false}
    >
      <LayoutSingleColumn
        rootClassName={css.pageRoot}
        isLandingPage={true}
        topbar={
          <CustomNavbar
            currentUser={currentUser}
            isAuthenticated={isAuthenticated}
            onLogout={onLogout}
            navLinks={START_PROJECT_NAV_LINKS}
          />
        }
        footer={<FooterContainer isLandingPage={true} />}
      >
        <HeroSection
          ppRef={ppRef}
          onPpRefChange={setPpRef}
          onStartProject={handleStartProject}
          onContinueAnyway={handleContinueAnyway}
        />
        <WhatHappensSection />
        <GetStartedSection />
        <PlanningDataSection
          ppRef={ppRef}
          onPpRefChange={setPpRef}
          onStartProject={handleStartProject}
          onContinueAnyway={handleContinueAnyway}
        />
        <QuestionSection />
        <BottomPageBanner />
        {!isAuthenticated && (
          <IdlePromptPopup
            delay={30000}
            suppressOnMouseMove={true}
            onManageDisableScrolling={onManageDisableScrolling}
            suppressOnFocus={['#ppRefInput']}
            content={
              <SignupForm
                onSubmit={onSubmitSignup}
                inProgress={false}
                termsAndConditions={<TermsAndConditions intl={intl} />}
                isCustomerSignup={true}
              />
            }
          />
        )}
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { isAuthenticated } = state.auth;
  return { currentUser, isAuthenticated };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onUpdateProfile: params => dispatch(updateProfile(params)),
  onSubmitSignup: (params, listingParams) => dispatch(signup(params, listingParams)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const StartYourProjectPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(StartYourProjectPageComponent);

export default StartYourProjectPage;
